import React from "react";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";

export default function TermsOfUsePage() {
  useScrollReveal();

  return (
    <WebsiteLayout>
      <div className="w-banner-dark">
        <section className="w-banner w-reveal" data-testid="terms-of-use-banner" style={{ paddingBottom: "4rem" }}>
          <div className="w-label">Legal</div>
          <h1 className="w-title" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
            Terms of <strong>Use</strong>
          </h1>
          <p className="w-subtitle" style={{ maxWidth: 520 }}>
            Rules, rights, and responsibilities governing your use of Tychi.
          </p>
        </section>
      </div>

      <section className="w-section" style={{ paddingTop: "3rem", paddingBottom: "4rem" }} data-testid="terms-of-use-page">
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div className="w-legal-content w-reveal">
            <p>
              These Terms of Use ("Terms") govern your access to and use of the Tychi software, related services, and the website tychi.co (collectively, "Tychi" or the "Software"). Tychi is operated by Tychi and its affiliates ("We," "Us," or "Our"). By accessing, registering for, or using Tychi, you ("User" or "you") agree to these Terms in their entirety. If you do not agree, you must not use Tychi.
            </p>

            <h2>Acceptance of Terms</h2>
            <p>
              By accessing or using Tychi in any capacity, you represent and warrant that you have read, understood, and agree to these Terms. If you do not accept or agree to these Terms, you may not use Tychi.
            </p>

            <h2>Changes to These Terms</h2>
            <p>
              We reserve the right to revise, modify, or update these Terms at any time, at Our sole discretion, without prior notice. Any such revisions will be effective immediately upon posting on tychi.co. Your continued use of Tychi after changes are posted constitutes your acceptance of the revised Terms.
            </p>

            <h2>Authorized Use</h2>
            <ol>
              <li>
                <strong>License:</strong> We grant you a limited, non-exclusive, revocable, and non-transferable license to use Tychi solely for lawful and intended purposes, subject to your full compliance with these Terms.
              </li>
              <li>
                <strong>Restrictions:</strong> You agree that you will not:
                <ul>
                  <li>Alter, reproduce, distribute, or resell the Software or any of its components without Our express written permission.</li>
                  <li>Reverse-engineer, decompile, disassemble, or otherwise attempt to derive source code or underlying ideas or algorithms of Tychi.</li>
                  <li>Introduce viruses, malicious code, or any other harmful components into Tychi.</li>
                  <li>Access or attempt to access any non-public systems, accounts, or data without authorization.</li>
                </ul>
              </li>
            </ol>

            <h2>User Obligations</h2>
            <p>By using Tychi, you agree to:</p>
            <ul>
              <li>Use Tychi only for lawful purposes and in accordance with any applicable local, national, or international laws and regulations.</li>
              <li>Keep your login credentials confidential and promptly notify Us of any unauthorized use or security breach.</li>
              <li>Provide accurate and current information when registering and whenever requested by Tychi.</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate your access to Tychi at any time if We determine, at Our sole discretion, that you have violated these Terms or any applicable law.
            </p>

            <h2>Intellectual Property Rights</h2>
            <ol>
              <li><strong>Ownership:</strong> All rights, title, and interest in Tychi — encompassing software code, features, designs, logos, trade names, trademarks, and other intellectual property — belong solely to Tychi and its affiliates. No right, title, or interest is granted to you other than the limited license described above.</li>
              <li><strong>Use of Marks:</strong> You may not use any Tychi trademarks, service marks, or branding without Our prior written consent, nor may you use them in any manner that implies endorsement, affiliation, or sponsorship without express authorization.</li>
            </ol>

            <h2>Third-Party Integrations</h2>
            <p>Tychi may provide or facilitate access to third-party applications, APIs, or services. By using these integrations:</p>
            <ul>
              <li>You acknowledge that Tychi has no control over and bears no responsibility for the performance, reliability, or security of such third-party services.</li>
              <li>Any issues or disputes relating to third-party services must be resolved directly between you and the relevant third-party provider.</li>
            </ul>

            <h2>Data Protection and Privacy</h2>
            <ol>
              <li><strong>Privacy Policy:</strong> Your use of Tychi is subject to Our Privacy Policy, which describes how We collect, use, process, and protect your personal data in compliance with applicable data protection laws. By using Tychi, you consent to all actions taken by Us with respect to your data consistent with the Privacy Policy.</li>
              <li><strong>Security Measures:</strong> We employ security protocols designed to protect your data. However, no method of electronic storage or transmission is entirely secure, and We disclaim any liability for unauthorized access or breaches beyond Our reasonable control.</li>
            </ol>

            <h2>Disclaimers of Liability</h2>
            <ol>
              <li><strong>No Warranties:</strong> TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, TYCHI IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY REPRESENTATIONS, GUARANTEES, OR WARRANTIES, WHETHER EXPRESS OR IMPLIED. WE MAKE NO WARRANTIES REGARDING THE ACCURACY, COMPLETENESS, QUALITY, RELIABILITY, OR AVAILABILITY OF TYCHI OR ANY INFORMATION OR OUTPUTS DERIVED FROM TYCHI.</li>
              <li><strong>User Assumption of Risk:</strong> BY ACCESSING OR USING TYCHI, YOU ACKNOWLEDGE AND AGREE THAT ANY RELIANCE ON OR USE OF INFORMATION, DATA, OR SERVICES AVAILABLE THROUGH TYCHI IS AT YOUR SOLE RISK. YOU ARE ENTIRELY RESPONSIBLE FOR ANY DAMAGE OR LOSS RESULTING FROM SUCH RELIANCE OR USE.</li>
              <li><strong>No Liability for Tychi:</strong> YOU EXPRESSLY AGREE THAT NEITHER TYCHI NOR ITS AFFILIATES SHALL BE RESPONSIBLE OR LIABLE FOR ANY FORM OF DAMAGE, LOSS, OR INJURY ARISING OUT OF OR RELATING TO YOUR USE, MISUSE, OR INABILITY TO USE TYCHI, REGARDLESS OF THE BASIS OF CLAIM. THIS INCLUDES, WITHOUT LIMITATION, DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY OTHER KIND OF LIABILITY.</li>
              <li><strong>Non-Exhaustive Disclaimer:</strong> THE ABOVE DISCLAIMERS APPLY TO ALL DAMAGES OR CAUSES OF ACTION ARISING FROM ANY AND ALL LEGAL THEORIES, INCLUDING BUT NOT LIMITED TO CONTRACT, TORT, STRICT LIABILITY, OR STATUTORY CLAIMS.</li>
            </ol>

            <h2>Termination</h2>
            <ol>
              <li><strong>Suspension or Termination:</strong> We may, at Our sole discretion and without prior notice, suspend or terminate your access to Tychi for any reason, including but not limited to your breach of these Terms.</li>
              <li><strong>Consequences of Termination:</strong> Upon termination, all rights granted to you under these Terms shall cease immediately, and you must promptly destroy or delete any content, materials, or copies thereof obtained through Tychi in your possession or control.</li>
            </ol>

            <h2>Governing Law and Jurisdiction</h2>
            <ol>
              <li><strong>Global Applicability:</strong> These Terms shall be governed and interpreted in accordance with principles of law generally applicable to agreements made and to be performed entirely online. You agree to comply with all local laws and regulations regarding your use of Tychi.</li>
              <li><strong>Dispute Resolution:</strong> Any dispute arising out of or in connection with these Terms shall, unless otherwise required by applicable local law, be subject to confidential binding arbitration or adjudication by a court of competent jurisdiction. By using Tychi, you irrevocably submit to such arbitration or jurisdiction, unless mandatory consumer protection laws in your local jurisdiction specify otherwise.</li>
            </ol>

            <h2>Global Compliance</h2>
            <p>You agree that you will not use Tychi in any manner prohibited by any applicable laws, regulations, or treaties. This includes, but is not limited to, compliance with:</p>
            <ul>
              <li>Export control and sanctions laws.</li>
              <li>Anti-corruption statutes, such as the U.S. Foreign Corrupt Practices Act (FCPA) and the UK Bribery Act.</li>
              <li>Data protection laws and regulations (e.g., GDPR, CCPA, or local equivalents).</li>
            </ul>

            <h2>Contact Us</h2>
            <p>
              If you have any questions, concerns, or notices related to these Terms or your use of Tychi, please contact:
            </p>
            <p>
              <strong>Tychi</strong><br />
              Email: <a href="mailto:portal@tychi.co">portal@tychi.co</a><br />
              Website: <a href="https://tychi.co" target="_blank" rel="noopener noreferrer">https://tychi.co</a>
            </p>
            <p style={{ marginTop: "2rem", opacity: 0.7, fontSize: "0.9em" }}>
              By continuing to access or use Tychi after the posting of any revised Terms, you acknowledge and agree to be bound by such modifications or updates. If you do not agree to any amended Terms, you must discontinue use of Tychi immediately.
            </p>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
